import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchStockSuggest } from "@/lib/api";

const MOOD = {
    positive: { label: "Positive", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    negative: { label: "Negative", cls: "bg-rose-50 text-rose-700 border-rose-200" },
    neutral: { label: "Neutral", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    none: { label: "No news today", cls: "bg-black/[0.03] text-black/40 border-black/10" },
};

function useDebounced(value, ms) {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
    return v;
}

// Text input with NSE symbol / company autocomplete (symbol · company · today's mood).
export const StockSuggestInput = ({ value, onChange, onPick, onEnter, testId = "stock-suggest", className = "", ...inputProps }) => {
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const wrapRef = useRef(null);
    const q = useDebounced((value || "").trim(), 200);
    const { data } = useQuery({
        queryKey: ["stock-suggest", q],
        queryFn: () => fetchStockSuggest(q),
        enabled: q.length >= 1 && open,
        staleTime: 60_000,
    });
    const items = (open && q && q === (value || "").trim() && data?.items) || [];

    useEffect(() => {
        const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);
    useEffect(() => setActive(-1), [q]);

    const pick = (it) => { setOpen(false); onChange(it.symbol); onPick(it.symbol); };
    const onKeyDown = (e) => {
        if (e.key === "ArrowDown" && items.length) { e.preventDefault(); setActive((a) => (a + 1) % items.length); }
        else if (e.key === "ArrowUp" && items.length) { e.preventDefault(); setActive((a) => (a <= 0 ? items.length - 1 : a - 1)); }
        else if (e.key === "Escape") setOpen(false);
        else if (e.key === "Enter") {
            if (active >= 0 && items[active]) { e.preventDefault(); pick(items[active]); }
            else { setOpen(false); onEnter?.(e); }
        }
    };

    return (
        <div ref={wrapRef} className="relative">
            <input
                {...inputProps}
                value={value}
                onChange={(e) => { onChange(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
                onKeyDown={onKeyDown}
                autoComplete="off"
                className={className}
                data-testid={`${testId}-input`}
            />
            {items.length > 0 && (
                <ul className="absolute z-50 left-0 right-0 mt-1 bg-white border border-black/10 rounded-sm shadow-[0_16px_40px_-12px_rgba(0,0,0,.25)] max-h-80 overflow-y-auto py-1" data-testid={`${testId}-list`} role="listbox">
                    {items.map((it, i) => <SuggestRow key={it.symbol} it={it} active={i === active} onPick={() => pick(it)} onHover={() => setActive(i)} testId={`${testId}-item-${it.symbol}`} />)}
                </ul>
            )}
        </div>
    );
};

const SuggestRow = ({ it, active, onPick, onHover, testId }) => {
    const m = MOOD[it.mood] || MOOD.none;
    return (
        <li role="option" aria-selected={active} onMouseDown={(e) => { e.preventDefault(); onPick(); }} onMouseEnter={onHover} data-testid={testId}
            className={`px-3 py-2 flex items-center gap-3 cursor-pointer transition-colors ${active ? "bg-[#22E578]/15" : "hover:bg-black/[0.03]"}`}>
            <div className="min-w-0 flex-1">
                <div className="font-sub text-[12px] font-bold tracking-[0.06em] text-black">{it.symbol}</div>
                <div className="font-body text-xs text-black/55 truncate">{it.company}</div>
            </div>
            <span className={`shrink-0 border rounded-sm px-1.5 py-0.5 text-[9px] font-sub uppercase tracking-[0.14em] ${m.cls}`} data-testid={`${testId}-mood`}>
                {m.label}{it.news_today ? ` · ${it.news_today}` : ""}
            </span>
        </li>
    );
};
